import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Multi-select filtering for the grouped collection catalog.
 *
 * Two modes, set by `data-mode` on the element:
 *
 * - `native`: options come from Shopify storefront filters (Search & Discovery
 *   app). Checkbox inputs are named with native `filter.*` params; on change the
 *   URL is updated and the section re-renders through the Section Rendering API,
 *   so the server filters the collection, renders the flat grid of matching
 *   color variants, and updates labels and counts.
 * - `client`: fallback until Search & Discovery filters are configured. The
 *   section pre-renders a hidden flat grid holding one card per color variant,
 *   and this component toggles card visibility client-side.
 *
 * @typedef {object} CollectionFiltersRefs
 * @property {HTMLElement} [groupedList] - Wrapper around the category-grouped layout (client mode).
 * @property {HTMLElement} [flatGrid] - Flat grid of per-color-variant cards (client mode).
 * @property {HTMLElement} [countLabel] - "N products" status element.
 * @property {HTMLDetailsElement[]} [dropdowns] - One details element per filter dropdown.
 * @property {HTMLElement[]} [toggleLabels] - Visible dropdown title elements.
 * @property {HTMLInputElement[]} [showAllInputs] - The "Show all" checkbox per dropdown.
 * @property {HTMLInputElement[]} [filterInputs] - The individual option checkboxes.
 * @property {HTMLElement[]} [filterItems] - Filterable flat-grid cells (client mode).
 * @property {HTMLInputElement} [priceMinInput] - Native mode price range minimum input.
 * @property {HTMLInputElement} [priceMaxInput] - Native mode price range maximum input.
 *
 * @extends {Component<CollectionFiltersRefs>}
 */
class CollectionFiltersComponent extends Component {
  #onPopState = () => {
    if (this.#isNative) this.#renderNativeSection();
  };

  connectedCallback() {
    super.connectedCallback();

    if (this.#isNative) {
      window.addEventListener('popstate', this.#onPopState);
    } else {
      this.#restoreFromUrl();
      this.#applyClient(false);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('popstate', this.#onPopState);
  }

  get #isNative() {
    return this.dataset.mode === 'native';
  }

  /**
   * Handles a change on an individual filter option checkbox.
   * @param {Event} event
   */
  handleOptionChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const filterKey = input.dataset.filterKey ?? '';
    const showAllInput = this.#showAllInput(filterKey);
    if (showAllInput) {
      showAllInput.checked = this.#selectedInputs(filterKey).length === 0;
    }

    if (this.#isNative) {
      this.#applyNative();
    } else {
      this.#applyClient();
    }
  }

  /**
   * Handles a change on a dropdown's "Show all" checkbox. Checking it clears the
   * dropdown's other selections; it cannot be unchecked directly.
   * @param {Event} event
   */
  handleShowAllChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const filterKey = input.dataset.filterKey ?? '';
    if (input.checked) {
      for (const optionInput of this.#optionInputs(filterKey)) {
        optionInput.checked = false;
      }
    } else if (this.#selectedInputs(filterKey).length === 0) {
      input.checked = true;
      return;
    }

    if (this.#isNative) {
      this.#applyNative();
    } else {
      this.#applyClient();
    }
  }

  /**
   * Handles a change on either price range input: clamps it to a
   * non-negative number that keeps min <= max, then re-applies native
   * filters. The active-red dropdown state and formatted label are computed
   * server-side and arrive with the section re-render, same as list-filter
   * checkboxes.
   * @param {Event} event
   */
  handlePriceChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    this.#clampPriceInput(input);
    this.#applyNative();
  }

  /**
   * Keeps the summary's aria-expanded state in sync with the dropdown, and
   * corrects its panel position for the viewport.
   * @param {Event} event
   */
  handleDropdownToggle(event) {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement)) return;

    details.querySelector('summary')?.setAttribute('aria-expanded', details.open ? 'true' : 'false');
    this.#correctPanelOverflow(details);
  }

  /**
   * Nudges a dropdown's panel back on-screen when it would overflow the
   * viewport's right edge (e.g. the right-most pill in a wrapped row on a
   * narrow viewport, or a pill whose label grew wider after a re-render).
   * Works for any dropdown — list filter or price — since both share the
   * same panel class. Called on toggle, and again after every native
   * section re-render, because a re-render can keep a dropdown open
   * (morph preserves the `open` attribute) without firing a fresh `toggle`
   * event, and the re-rendered content (e.g. a wider "$45 – $60" label) can
   * introduce overflow that the original toggle-time correction never saw.
   * @param {HTMLDetailsElement} details
   */
  #correctPanelOverflow(details) {
    const panel = details.querySelector('.collection-filters__panel');
    if (!(panel instanceof HTMLElement)) return;

    panel.style.removeProperty('left');
    if (!details.open) return;

    const overflowRight = panel.getBoundingClientRect().right - window.innerWidth;
    if (overflowRight > 0) {
      panel.style.left = `${-(overflowRight + 16)}px`;
    }
  }

  /**
   * Closes an open dropdown on Escape and restores focus to its toggle.
   * @param {KeyboardEvent} event
   */
  handleDropdownKeydown(event) {
    if (event.key !== 'Escape') return;

    const details = event.target instanceof Element ? event.target.closest('details') : null;
    if (!details?.open) return;

    details.removeAttribute('open');
    details.querySelector('summary')?.focus();
  }

  /**
   * @param {string} filterKey
   * @returns {HTMLInputElement | undefined}
   */
  #showAllInput(filterKey) {
    return (this.refs.showAllInputs ?? []).find((input) => input.dataset.filterKey === filterKey);
  }

  /**
   * @param {string} filterKey
   * @returns {HTMLInputElement[]}
   */
  #optionInputs(filterKey) {
    return (this.refs.filterInputs ?? []).filter((input) => input.dataset.filterKey === filterKey);
  }

  /**
   * @param {string} filterKey
   * @returns {HTMLInputElement[]}
   */
  #selectedInputs(filterKey) {
    return this.#optionInputs(filterKey).filter((input) => input.checked);
  }

  /**
   * Clamps a price input to a non-negative number and keeps min <= max by
   * pulling the other bound to match. Empty/unparseable input clears to "".
   * A single-currency, decimal-dollar clamp is enough for this store; a
   * multi-currency deployment would need the minor-unit-aware parsing that
   * Horizon's own price-facet-component implements.
   * @param {HTMLInputElement} input
   */
  #clampPriceInput(input) {
    const { priceMinInput, priceMaxInput } = this.refs;
    const value = Number.parseFloat(input.value);

    if (input.value.trim() === '' || Number.isNaN(value)) {
      input.value = '';
      return;
    }

    input.value = String(Math.max(0, value));

    const min = priceMinInput ? Number.parseFloat(priceMinInput.value) : NaN;
    const max = priceMaxInput ? Number.parseFloat(priceMaxInput.value) : NaN;
    if (Number.isNaN(min) || Number.isNaN(max) || min <= max) return;

    if (input === priceMinInput && priceMaxInput) priceMaxInput.value = priceMinInput.value;
    else if (input === priceMaxInput && priceMinInput) priceMinInput.value = priceMaxInput.value;
  }

  /**
   * Native mode: mirrors checkbox and price-range state into `filter.*` URL
   * params and lets the server re-render the section with the filtered
   * collection. Generic across however many list filters and whether a price
   * filter is present, so newly configured Search & Discovery filters work
   * without a theme change.
   */
  #applyNative() {
    const url = new URL(window.location.href);
    const { priceMinInput, priceMaxInput } = this.refs;

    const paramNames = new Set();
    for (const dropdown of this.refs.dropdowns ?? []) {
      if (dropdown.dataset.paramName) paramNames.add(dropdown.dataset.paramName);
    }
    for (const input of this.refs.filterInputs ?? []) {
      if (input.name) paramNames.add(input.name);
    }
    if (priceMinInput?.name) paramNames.add(priceMinInput.name);
    if (priceMaxInput?.name) paramNames.add(priceMaxInput.name);
    for (const paramName of paramNames) {
      url.searchParams.delete(paramName);
    }

    for (const input of this.refs.filterInputs ?? []) {
      if (input.checked && input.name) {
        url.searchParams.append(input.name, input.value);
      }
    }
    if (priceMinInput?.value.trim() && priceMinInput.name) {
      url.searchParams.set(priceMinInput.name, priceMinInput.value.trim());
    }
    if (priceMaxInput?.value.trim() && priceMaxInput.name) {
      url.searchParams.set(priceMaxInput.name, priceMaxInput.value.trim());
    }

    url.searchParams.delete('page');
    window.history.pushState({}, '', url);

    this.#renderNativeSection();
  }

  /**
   * Re-renders the section for the current URL and re-opens the dropdown the
   * shopper was interacting with (the morphed markup arrives closed).
   */
  async #renderNativeSection() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const openFilterKey = (this.refs.dropdowns ?? []).find((dropdown) => dropdown.open)?.dataset.filterKey;

    await sectionRenderer.renderSection(sectionId);

    if (!openFilterKey) return;
    const dropdown = document.querySelector(
      `collection-filters-component details[data-filter-key="${openFilterKey}"]`,
    );
    if (!(dropdown instanceof HTMLDetailsElement)) return;

    if (!dropdown.open) dropdown.open = true;
    this.#correctPanelOverflow(dropdown);
  }

  /**
   * @returns {Record<string, string[]>} Selected values keyed by filter key.
   */
  #clientState() {
    const state = {};
    for (const dropdown of this.refs.dropdowns ?? []) {
      const filterKey = dropdown.dataset.filterKey ?? '';
      state[filterKey] = this.#selectedInputs(filterKey).map((input) => input.value);
    }
    return state;
  }

  /**
   * @param {HTMLElement} item
   * @param {Record<string, string[]>} state
   * @returns {boolean}
   */
  #matches(item, state) {
    for (const [filterKey, selected] of Object.entries(state)) {
      if (selected.length === 0) continue;

      const itemValues = (item.getAttribute(`data-filter-${filterKey}`) ?? '').split(' ');
      if (!itemValues.some((value) => value !== '' && selected.includes(value))) {
        return false;
      }
    }
    return true;
  }

  /**
   * Client mode: applies the current filter state to the layout, cards, labels,
   * count, and URL.
   * @param {boolean} [updateUrl]
   */
  #applyClient(updateUrl = true) {
    const { groupedList, flatGrid, countLabel } = this.refs;
    if (!groupedList || !flatGrid || !countLabel) return;

    const state = this.#clientState();
    const anyActive = Object.values(state).some((selected) => selected.length > 0);

    groupedList.hidden = anyActive;
    flatGrid.hidden = !anyActive;

    let visibleCardCount = 0;
    for (const item of this.refs.filterItems ?? []) {
      const matches = anyActive && this.#matches(item, state);
      item.hidden = !matches;
      if (matches) visibleCardCount += 1;
    }

    for (const dropdown of this.refs.dropdowns ?? []) {
      const filterKey = dropdown.dataset.filterKey ?? '';
      const selectedCount = state[filterKey]?.length ?? 0;
      dropdown.classList.toggle('collection-filters__dropdown--active', selectedCount > 0);

      const label = (this.refs.toggleLabels ?? []).find((element) => element.dataset.filterKey === filterKey);
      if (label) {
        const baseLabel = label.dataset.baseLabel ?? '';
        label.textContent = selectedCount > 0 ? `${baseLabel} (${selectedCount})` : baseLabel;
      }
    }

    const totalCount = Number(this.dataset.totalCount ?? '0');
    this.#renderCount(anyActive ? visibleCardCount : totalCount);

    if (updateUrl) this.#syncClientUrl(state);
  }

  /**
   * @param {number} count
   */
  #renderCount(count) {
    const { countLabel } = this.refs;
    if (!countLabel) return;

    const template = count === 1 ? countLabel.dataset.countOne : countLabel.dataset.countOther;
    if (template) {
      countLabel.textContent = template.replace(/\d+/, String(count));
    }
  }

  /**
   * Client mode: mirrors filter state into URL query parameters without adding
   * history entries.
   * @param {Record<string, string[]>} state
   */
  #syncClientUrl(state) {
    const url = new URL(window.location.href);
    for (const [filterKey, selected] of Object.entries(state)) {
      if (selected.length > 0) {
        url.searchParams.set(filterKey, selected.join(','));
      } else {
        url.searchParams.delete(filterKey);
      }
    }
    window.history.replaceState({}, '', url);
  }

  /**
   * Client mode: restores checkbox state from URL query parameters.
   */
  #restoreFromUrl() {
    const params = new URLSearchParams(window.location.search);
    for (const dropdown of this.refs.dropdowns ?? []) {
      const filterKey = dropdown.dataset.filterKey ?? '';
      const values = (params.get(filterKey) ?? '').split(',').filter(Boolean);
      if (values.length === 0) continue;

      let anyChecked = false;
      for (const input of this.#optionInputs(filterKey)) {
        input.checked = values.includes(input.value);
        anyChecked = anyChecked || input.checked;
      }

      const showAllInput = this.#showAllInput(filterKey);
      if (showAllInput) showAllInput.checked = !anyChecked;
    }
  }
}

if (!customElements.get('collection-filters-component')) {
  customElements.define('collection-filters-component', CollectionFiltersComponent);
}
